import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {toast} from "sonner";
import {PageWrapper} from "@/layout/components/PageWrapper.tsx";
import {PageHeader} from "@/layout/components/PageHeader.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {SearchableSelect} from "@/components/ui/searchable-select.tsx";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog.tsx";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import {
    ClipboardDocumentListIcon, EllipsisVerticalIcon, MagnifyingGlassIcon, PlusIcon, TrashIcon,
} from "@heroicons/react/24/outline";
import {cn} from "@/lib/utils.ts";
import {
    useBulkCopySClassTestsMutation, useBulkMoveSClassTestsMutation,
    useCreateSClassTestMutation, useDeleteSClassTestMutation,
    useGetSClassOptionsQuery, useGetSClassTestsQuery, useLazyGetIvrFieldsQuery,
} from "@/store/sclassTest.ts";
import {SClassTest, formatSClassOptionLabel} from "@/types/SClassTest.ts";
import {formatApiError} from "@/pages/sclass-test/apiError.ts";

const ALL_SCLASS_VALUE = "all";

export function SClassTestListPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [sclassFilter, setSclassFilter] = useState(ALL_SCLASS_VALUE);
    const [toDelete, setToDelete] = useState<SClassTest | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [newDescription, setNewDescription] = useState("");
    const [newSclass, setNewSclass] = useState<string | undefined>(undefined);

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkTargetMode, setBulkTargetMode] = useState<'copy' | 'move' | null>(null);
    const [bulkTargetSclass, setBulkTargetSclass] = useState<string | undefined>(undefined);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    const {data: tests, isLoading} = useGetSClassTestsQuery();
    const {data: sclassOptions, isLoading: isLoadingSclassOptions} = useGetSClassOptionsQuery();
    const [deleteSClassTest, {isLoading: isDeleting}] = useDeleteSClassTestMutation();
    const [createSClassTest, {isLoading: isCreating}] = useCreateSClassTestMutation();
    const [bulkCopySClassTests, {isLoading: isBulkCopying}] = useBulkCopySClassTestsMutation();
    const [bulkMoveSClassTests, {isLoading: isBulkMoving}] = useBulkMoveSClassTestsMutation();
    const [triggerGetIvrFields] = useLazyGetIvrFieldsQuery();

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (tests || []).filter((t) => {
            if (sclassFilter !== ALL_SCLASS_VALUE && String(t.sclass ?? "") !== sclassFilter) return false;
            if (!term) return true;
            const title = (t.description || `מקבץ #${t.id}`).toLowerCase();
            const sclassName = (t.sclass_name || "").toLowerCase();
            return title.includes(term) || sclassName.includes(term);
        });
    }, [tests, search, sclassFilter]);

    function openCreateDialog() {
        setNewDescription("");
        setNewSclass(undefined);
        setCreateOpen(true);
    }

    async function handleCreate() {
        try {
            const created = await createSClassTest({
                description: newDescription || null,
                sclass: newSclass ? Number(newSclass) : null,
            }).unwrap();
            toast.success('מקבץ השאלות נוצר בהצלחה');
            setCreateOpen(false);
            navigate(`/sclass-test/${created.id}/edit`);
        } catch (e: any) {
            toast.error('שגיאה ביצירת מקבץ השאלות', {description: formatApiError(e)});
        }
    }

    async function handleConfirmDelete() {
        if (!toDelete) return;
        try {
            await deleteSClassTest({id: toDelete.id}).unwrap();
            toast.success('מקבץ השאלות נמחק בהצלחה');
        } catch (e: any) {
            toast.error('שגיאה במחיקת מקבץ השאלות', {description: formatApiError(e)});
        } finally {
            setToDelete(null);
        }
    }

    function toggleSelected(id: number) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function clearSelection() {
        setSelectedIds(new Set());
    }

    const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id));
    const someFilteredSelected = filtered.some((t) => selectedIds.has(t.id));

    function toggleSelectAll() {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filtered.forEach((t) => next.delete(t.id));
            } else {
                filtered.forEach((t) => next.add(t.id));
            }
            return next;
        });
    }

    async function handleConfirmBulkDelete() {
        const ids = Array.from(selectedIds);
        setIsBulkDeleting(true);
        try {
            const results = await Promise.allSettled(ids.map((id) => deleteSClassTest({id}).unwrap()));
            const failed = results.filter((r) => r.status === 'rejected').length;
            const succeeded = results.length - failed;
            if (succeeded) toast.success(`נמחקו ${succeeded} מקבצי שאלות`);
            if (failed) toast.error(`נכשלה מחיקה של ${failed} מקבצי שאלות`);
            clearSelection();
        } finally {
            setIsBulkDeleting(false);
            setBulkDeleteOpen(false);
        }
    }

    function openBulkTargetDialog(mode: 'copy' | 'move') {
        setBulkTargetSclass(undefined);
        setBulkTargetMode(mode);
    }

    async function handleConfirmBulkTarget() {
        if (!bulkTargetMode || !bulkTargetSclass) return;
        const ids = Array.from(selectedIds);
        try {
            if (bulkTargetMode === 'copy') {
                const res = await bulkCopySClassTests({ids, target_sclass_id: Number(bulkTargetSclass)}).unwrap();
                toast.success(`הועתקו ${res.copied_count} שאלונים`);
            } else {
                const res = await bulkMoveSClassTests({ids, target_sclass_id: Number(bulkTargetSclass)}).unwrap();
                toast.success(`הועברו ${res.moved_count} שאלונים, ביחד עם ${res.results_moved} תוצאות`);
            }
            setBulkTargetMode(null);
            clearSelection();
        } catch (e: any) {
            toast.error(bulkTargetMode === 'copy' ? 'שגיאה בהעתקת השאלונים' : 'שגיאה בהעברת השאלונים', {description: formatApiError(e)});
        }
    }

    async function handleShowResults(t: SClassTest, e: React.MouseEvent) {
        e.stopPropagation();
        try {
            const fields = await triggerGetIvrFields(t.id).unwrap();
            const fieldsStr = fields.map((f) => f.desc).join(',');
            window.open(`/learning/result/?sclass_test_filter=${t.id}&fields=${encodeURIComponent(fieldsStr)}`, '_blank');
        } catch (e: any) {
            toast.error('שגיאה בטעינת השאלות', {description: formatApiError(e)});
        }
    }

    function handleShowResultsAuto(t: SClassTest, e: React.MouseEvent) {
        e.stopPropagation();
        window.open(`/learning/result/?sclass_test_filter=${t.id}&fields_auto=True`, '_blank');
    }

    function handleShowNotChecked(t: SClassTest, e: React.MouseEvent) {
        e.stopPropagation();
        window.open(`/learning/ivrfield/?sclass_test__id__exact=${t.id}&show_statistic=yes`, '_blank');
    }

    return (
        <PageWrapper>
            <PageHeader title="מקבצי שאלות"/>

            <div className="flex items-center justify-between gap-4 flex-wrap" dir="rtl">
                <div className="flex items-center gap-3 flex-wrap flex-1 min-w-[220px]">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                        <MagnifyingGlassIcon
                            className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"/>
                        <Input
                            placeholder="חיפוש לפי שם או שלוחה…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pr-9"
                            dir="rtl"
                        />
                    </div>
                    <SearchableSelect
                        value={sclassFilter}
                        onValueChange={setSclassFilter}
                        isLoading={isLoadingSclassOptions}
                        searchPlaceholder="חיפוש שלוחה לפי שם או מספר..."
                        emptyText="לא נמצאו שלוחות"
                        className="w-[220px]"
                        options={[
                            {value: ALL_SCLASS_VALUE, label: 'כל השלוחות'},
                            ...(sclassOptions ?? []).map((opt) => ({
                                value: String(opt.id),
                                label: formatSClassOptionLabel(opt),
                            })),
                        ]}
                    />
                </div>
                <Button onClick={openCreateDialog} className="gap-1.5">
                    <PlusIcon className="size-4"/>
                    מקבץ חדש
                </Button>
            </div>

            {!isLoading && !!tests?.length && (
                <div className="flex items-center gap-3 flex-wrap -mt-2" dir="rtl">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                        <Checkbox
                            checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
                            onCheckedChange={toggleSelectAll}
                        />
                        בחר הכל
                    </label>
                    {selectedIds.size > 0 ? (
                        <>
                            <span className="text-sm font-medium text-foreground">{selectedIds.size} נבחרו</span>
                            <Button size="sm" variant="outline" onClick={() => openBulkTargetDialog('copy')}>
                                העתק לשלוחה
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openBulkTargetDialog('move')}>
                                העבר לשלוחה
                            </Button>
                            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive"
                                    onClick={() => setBulkDeleteOpen(true)}>
                                מחק
                            </Button>
                            <Button size="sm" variant="ghost" onClick={clearSelection}>
                                בטל בחירה
                            </Button>
                        </>
                    ) : (
                        <span className="text-sm text-muted-foreground">
                            {search || sclassFilter !== ALL_SCLASS_VALUE
                                ? `${filtered.length} תוצאות מתוך ${tests.length} מקבצים`
                                : `${tests.length} מקבצי שאלות`}
                        </span>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-20">
                    <Spinner/>
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground rounded-xl border border-dashed">
                    <ClipboardDocumentListIcon className="size-10 mx-auto mb-3 opacity-40"/>
                    <p>{search || sclassFilter !== ALL_SCLASS_VALUE ? 'לא נמצאו תוצאות לחיפוש' : 'אין מקבצי שאלות עדיין'}</p>
                    {!search && sclassFilter === ALL_SCLASS_VALUE && (
                        <Button variant="link" onClick={openCreateDialog} className="mt-1">
                            צור את המקבץ הראשון
                        </Button>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2" dir="rtl">
                    {filtered.map((t) => {
                        const title = t.description || `מקבץ #${t.id}`;
                        const isSelected = selectedIds.has(t.id);
                        return (
                            <div
                                key={t.id}
                                className={cn(
                                    "group flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3.5 transition-colors hover:bg-neutral-50 hover:border-neutral-300 cursor-pointer",
                                    isSelected && "border-light-blue-300 border-r-4 border-r-light-blue-600 bg-light-blue-50"
                                )}
                                onClick={() => navigate(`/sclass-test/${t.id}/edit`)}
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <Checkbox
                                        checked={isSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onCheckedChange={() => toggleSelected(t.id)}
                                    />
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <ClipboardDocumentListIcon className="size-4"/>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
                                            {title}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                            {t.sclass_name ? `שלוחה: ${t.sclass_name}` : 'ללא שלוחה'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <Badge variant="outline" className="border-neutral-300 text-neutral-600">
                                        {t.questions_count ?? 0} שאלות
                                    </Badge>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="text-neutral-500"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <EllipsisVerticalIcon className="size-4"/>
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="text-right">
                                            <DropdownMenuItem onClick={(e) => handleShowResults(t, e)}>
                                                תוצאות
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => handleShowResultsAuto(t, e)}>
                                                תוצאות (עמודות אוטומטיות)
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => handleShowNotChecked(t, e)}>
                                                הצג כמה שאלות עדיין לא נבדקו
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="text-destructive hover:text-destructive"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setToDelete(t);
                                        }}
                                    >
                                        <TrashIcon className="size-4"/>
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent dir="rtl">
                    <DialogHeader>
                        <DialogTitle>מקבץ שאלות חדש</DialogTitle>
                    </DialogHeader>
                    <div className="px-6 space-y-4">
                        <div className="space-y-1">
                            <Label>שם מקבץ השאלות</Label>
                            <Input
                                value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)}
                                placeholder="לדוגמה: מבחן שבועי"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>שלוחה (אופציונלי)</Label>
                            <SearchableSelect
                                value={newSclass}
                                onValueChange={setNewSclass}
                                isLoading={isLoadingSclassOptions}
                                placeholder="בחר שלוחה"
                                searchPlaceholder="חיפוש שלוחה לפי שם או מספר..."
                                emptyText="לא נמצאו שלוחות"
                                options={(sclassOptions ?? []).map((opt) => ({
                                    value: String(opt.id),
                                    label: formatSClassOptionLabel(opt),
                                }))}
                            />
                        </div>
                    </div>
                    <DialogFooter className="px-6 pb-6">
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>ביטול</Button>
                        <Button onClick={handleCreate} disabled={isCreating}>
                            {isCreating ? <Spinner size="sm"/> : 'יצירה'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>מחיקת מקבץ שאלות</AlertDialogTitle>
                        <AlertDialogDescription>
                            האם אתה בטוח שברצונך למחוק את מקבץ השאלות "{toDelete?.description || `מקבץ #${toDelete?.id}`}"?
                            פעולה זו אינה הפיכה{toDelete?.questions_count ? `, וה-${toDelete.questions_count} שאלות שבתוכו יישארו ללא שיוך למקבץ` : ''}.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>ביטול</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isDeleting}
                            onClick={handleConfirmDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            מחיקה
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !open && setBulkDeleteOpen(false)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>מחיקת מקבצי שאלות</AlertDialogTitle>
                        <AlertDialogDescription>
                            האם אתה בטוח שברצונך למחוק {selectedIds.size} מקבצי שאלות? פעולה זו אינה הפיכה.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>ביטול</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isBulkDeleting}
                            onClick={handleConfirmBulkDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            מחיקה
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!bulkTargetMode} onOpenChange={(open) => !open && setBulkTargetMode(null)}>
                <DialogContent dir="rtl">
                    <DialogHeader>
                        <DialogTitle>{bulkTargetMode === 'copy' ? 'העתקת שאלונים לשלוחה' : 'העברת שאלונים לשלוחה'}</DialogTitle>
                    </DialogHeader>
                    <div className="px-6 space-y-4">
                        <div className="space-y-1">
                            <Label>שלוחת יעד</Label>
                            <SearchableSelect
                                value={bulkTargetSclass}
                                onValueChange={setBulkTargetSclass}
                                isLoading={isLoadingSclassOptions}
                                placeholder="בחר שלוחה"
                                searchPlaceholder="חיפוש שלוחה לפי שם או מספר..."
                                emptyText="לא נמצאו שלוחות"
                                options={(sclassOptions ?? []).map((opt) => ({
                                    value: String(opt.id),
                                    label: formatSClassOptionLabel(opt),
                                }))}
                            />
                        </div>
                    </div>
                    <DialogFooter className="px-6 pb-6">
                        <Button variant="outline" onClick={() => setBulkTargetMode(null)}>ביטול</Button>
                        <Button
                            onClick={handleConfirmBulkTarget}
                            disabled={!bulkTargetSclass || isBulkCopying || isBulkMoving}
                        >
                            {(isBulkCopying || isBulkMoving) ? <Spinner size="sm"/> : (bulkTargetMode === 'copy' ? 'העתקה' : 'העברה')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageWrapper>
    );
}

export default SClassTestListPage;
